import type { TFunction } from "i18next";
import { u2000BuildAppRouteHref } from "../../../../../u2000Core/tools/u2000OpenAppRouteInNewTab";
import { u3125FormatCurrency } from "../../../../../u3125Facture/U3125";
import { u3125FormatBillingSourceDisplayAmount } from "../../../../../u3125Facture/U3125Service";
import { NS } from "../../../../U3121";
import type {
  U3121BillingCreditAdvanceDocumentsSummary,
  U3121BillingDocumentNature,
  U3121BillingSummary,
  U3121BillingVoidNatureBucket,
} from "../../../../U3121Service";
import U3121BillingSummaryMetricCard from "./U3121BillingSummaryMetricCard";
import U3121BillingSummaryNoAccessPanel from "./U3121BillingSummaryNoAccessPanel";

interface U3121BillingSummaryDocumentsSectionProps {
  summary: U3121BillingSummary;
  onOpenDetail: (documentNature: U3121BillingDocumentNature) => void;
  t: TFunction;
}

function formatCreditSummaryAmount(value: number): string {
  return u3125FormatCurrency(u3125FormatBillingSourceDisplayAmount(value, "CREDIT"));
}

function creditNotesMetrics(
  creditNotes: U3121BillingCreditAdvanceDocumentsSummary,
  t: TFunction,
): { label: string; value: string | number }[] {
  const cn = creditNotes;
  return [
    { label: t("lblDocumentCount", { ns: NS }), value: cn.documentCount },
    {
      label: t("lblTotalOriginalAmountBeforeTax", { ns: NS }),
      value: formatCreditSummaryAmount(cn.totalOriginalAmount),
    },
    {
      label: t("lblTotalOriginalAmountTaxable", { ns: NS }),
      value: formatCreditSummaryAmount(cn.totalOriginalAmountTaxable),
    },
    {
      label: t("lblTotalOriginalAmountNonTaxable", { ns: NS }),
      value: formatCreditSummaryAmount(cn.totalOriginalAmountNonTaxable),
    },
    {
      label: t("lblTotalRemainingAmountBeforeTax", { ns: NS }),
      value: formatCreditSummaryAmount(cn.totalRemainingAmount),
    },
    {
      label: t("lblTotalRemainingAmountTaxable", { ns: NS }),
      value: formatCreditSummaryAmount(cn.totalRemainingAmountTaxable),
    },
    {
      label: t("lblTotalRemainingAmountNonTaxable", { ns: NS }),
      value: formatCreditSummaryAmount(cn.totalRemainingAmountNonTaxable),
    },
  ];
}

function voidNatureMetric(
  bucket: U3121BillingVoidNatureBucket,
  labelKey: string,
  t: TFunction,
): { label: string; value: string } | null {
  if (bucket.count <= 0) return null;
  return {
    label: t(labelKey, { count: bucket.count, ns: NS }),
    value: u3125FormatCurrency(bucket.amount),
  };
}

function U3121BillingSummaryDocumentsSection({ summary, onOpenDetail, t }: U3121BillingSummaryDocumentsSectionProps) {
  if (summary.billingDocuments == null) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-foreground">{t("sectionBillingSummaryDocuments", { ns: NS })}</h3>
        <U3121BillingSummaryNoAccessPanel messageKey="msgBillingSummaryNoInvoiceAccess" t={t} />
      </section>
    );
  }

  const { standard, creditNotes, advances, void: voidDocs } = summary.billingDocuments;
  const standardGrossBeforeCredit = standard.totalGrossAmountInvoiceTaxable + standard.totalGrossAmountInvoiceNonTaxable;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-foreground">{t("sectionBillingSummaryDocuments", { ns: NS })}</h3>
        <a
          href={u2000BuildAppRouteHref("/U3125")}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-600 hover:underline"
        >
          {t("btnGoToInvoicing", { ns: NS })}
        </a>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <U3121BillingSummaryMetricCard
          title={t("lblInvoicesStandard", { ns: NS })}
          metrics={[
            { label: t("lblDocumentCount", { ns: NS }), value: standard.documentCount },
            { label: t("lblTotalGrossBeforeCredit", { ns: NS }), value: u3125FormatCurrency(standardGrossBeforeCredit) },
            { label: t("lblTotalInvoicedAmount", { ns: NS }), value: u3125FormatCurrency(standard.totalInvoicedAmount) },
            { label: t("lblTotalPaymentsReceived", { ns: NS }), value: u3125FormatCurrency(standard.totalPaymentsReceived) },
            { label: t("lblBalanceDue", { ns: NS }), value: u3125FormatCurrency(standard.totalBalanceDue) },
          ]}
          onViewDetail={() => onOpenDetail("STANDARD")}
          t={t}
        />
        <U3121BillingSummaryMetricCard
          title={t("lblCreditNotes", { ns: NS })}
          metrics={creditNotesMetrics(creditNotes, t)}
          onViewDetail={() => onOpenDetail("CREDIT")}
          t={t}
        />
        <U3121BillingSummaryMetricCard
          title={t("lblAdvances", { ns: NS })}
          metrics={[
            { label: t("lblDocumentCount", { ns: NS }), value: advances.documentCount },
            { label: t("lblTotalOriginalAmount", { ns: NS }), value: u3125FormatCurrency(advances.totalOriginalAmount) },
            { label: t("lblTotalAppliedAmount", { ns: NS }), value: u3125FormatCurrency(advances.totalAppliedAmount) },
            { label: t("lblTotalRemainingAmount", { ns: NS }), value: u3125FormatCurrency(advances.totalRemainingAmount) },
            ...(advances.totalPaymentsReceived != null
              ? [{ label: t("lblTotalPaymentsReceived", { ns: NS }), value: u3125FormatCurrency(advances.totalPaymentsReceived) }]
              : []),
          ]}
          onViewDetail={() => onOpenDetail("ADVANCE")}
          t={t}
        />
        <U3121BillingSummaryMetricCard
          title={t("lblInvoicesVoid", { ns: NS })}
          metrics={[
            { label: t("lblDocumentCount", { ns: NS }), value: voidDocs.documentCount },
            ...[
              voidNatureMetric(voidDocs.byNature.standard, "lblVoidByNatureStandard", t),
              voidNatureMetric(voidDocs.byNature.credit, "lblVoidByNatureCredit", t),
              voidNatureMetric(voidDocs.byNature.advance, "lblVoidByNatureAdvance", t),
              voidNatureMetric(voidDocs.byNature.wipWriteOff, "lblVoidByNatureWipWriteOff", t),
            ].filter((m): m is { label: string; value: string } => m != null),
            { label: t("lblVoidTotalMixed", { ns: NS }), value: u3125FormatCurrency(voidDocs.totalInvoicedAmount) },
          ]}
          onViewDetail={() => onOpenDetail("VOID")}
          t={t}
        />
      </div>
    </section>
  );
}

export default U3121BillingSummaryDocumentsSection;
